import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Tag, ToggleLeft, ToggleRight, Trash2, Calendar, Eye, RefreshCw } from 'lucide-react';
import { usePromotions, type Promotion } from '../../api/queries/promotion.queries';
import { useTogglePromotion, useDeletePromotion } from '../../api/mutations/promotion.mutations';
import { useAuthStore } from '../../stores/auth.store';
import PromotionModal from './components/PromotionModal';
import PromotionDetailModal from './components/PromotionDetailModal';

type TabType = 'all' | 'active' | 'inactive' | 'expired' | 'upcoming';

function PromotionsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();

  // Tab and filter state
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Query
  const { data: promotions, refetch } = usePromotions();

  // Filter promotions based on tab
  const filteredPromotions = promotions?.filter((promo) => {
    const isExpired = new Date(promo.endDate) < new Date();
    const isUpcoming = new Date(promo.startDate) > new Date();

    // Tab filter
    switch (activeTab) {
      case 'active':
        return promo.isActive && !isExpired && !isUpcoming;
      case 'inactive':
        return !promo.isActive && !isExpired;
      case 'expired':
        return isExpired;
      case 'upcoming':
        return isUpcoming;
      default:
        break;
    }

    // Type filter
    if (typeFilter && promo.type !== typeFilter) return false;

    return true;
  }) || [];

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  // Mutations declared at component level
  const toggleMutation = useTogglePromotion(selectedPromotion?._id || '');
  const deleteMutation = useDeletePromotion(selectedPromotion?._id || '');

  const handleToggle = async (promotion: Promotion) => {
    setSelectedPromotion(promotion);
    try {
      await toggleMutation.mutateAsync(!promotion.isActive);
      refetch();
    } catch (error) {
      console.error('Failed to toggle promotion:', error);
    }
  };

  const handleDelete = async (promotion: Promotion) => {
    if (!confirm(t('promotions.confirmDelete'))) return;

    setSelectedPromotion(promotion);
    try {
      await deleteMutation.mutateAsync();
      refetch();
    } catch (error) {
      console.error('Failed to delete promotion:', error);
    }
  };

  const handleViewDetail = (promotion: Promotion) => {
    setSelectedPromotion(promotion);
    setShowDetailModal(true);
  };

  const handleEdit = (promotion: Promotion) => {
    setSelectedPromotion(promotion);
    setShowEditModal(true);
    setShowDetailModal(false);
  };

  const handleModalSuccess = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDetailModal(false);
    setSelectedPromotion(null);
    refetch();
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDiscountLabel = (promotion: Promotion) => {
    const { discount } = promotion;
    if (discount.type === 'percentage') {
      return `${discount.value}%`;
    } else if (discount.type === 'fixed_amount') {
      return `฿${discount.value}`;
    } else if (discount.type === 'fixed_price') {
      return `฿${discount.value} fixed`;
    } else if (discount.type === 'buy_x_get_y') {
      return 'Buy X Get Y';
    }
    return discount.type;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'discount':
        return 'text-blue-600 bg-blue-50';
      case 'bundle':
        return 'text-purple-600 bg-purple-50';
      case 'buy_x_get_y':
        return 'text-green-600 bg-green-50';
      case 'seasonal':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const isExpired = (endDate: string) => {
    return new Date(endDate) < new Date();
  };

  const isUpcoming = (startDate: string) => {
    return new Date(startDate) > new Date();
  };

  const canManage = user?.role === 'HQ_ADMIN' || user?.role === 'HQ_PURCHASING';

  const tabs = [
    { id: 'all', label: t('common.all'), count: promotions?.length || 0 },
    { id: 'active', label: t('promotions.active'), count: promotions?.filter(p => p.isActive && !isExpired(p.endDate) && !isUpcoming(p.startDate)).length || 0 },
    { id: 'inactive', label: t('promotions.inactive'), count: promotions?.filter(p => !p.isActive && !isExpired(p.endDate)).length || 0 },
    { id: 'upcoming', label: t('promotions.upcoming'), count: promotions?.filter(p => isUpcoming(p.startDate)).length || 0 },
    { id: 'expired', label: t('promotions.expired'), count: promotions?.filter(p => isExpired(p.endDate)).length || 0 },
  ];

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('promotions.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('promotions.subtitle')}</p>
          </div>
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg hover:bg-secondary transition-colors"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b bg-card">
        <div className="flex gap-1 p-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-secondary'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  activeTab === tab.id
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b bg-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">{t('promotions.type')}:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-lg border bg-background px-3 py-1.5 text-sm"
          >
            <option value="">{t('common.all')}</option>
            <option value="discount">{t('promotions.discount')}</option>
            <option value="bundle">{t('promotions.bundle')}</option>
            <option value="buy_x_get_y">{t('promotions.buy_x_get_y')}</option>
            <option value="seasonal">{t('promotions.seasonal')}</option>
          </select>
        </div>

        {canManage && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('promotions.createPromotion')}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto space-y-3">
          {filteredPromotions.map((promotion) => (
            <div
              key={promotion._id}
              className={`rounded-lg border bg-card p-4 hover:bg-secondary/50 transition-colors ${
                isExpired(promotion.endDate) ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="mt-1">
                  <Tag className="h-5 w-5 text-primary" />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium">{getLangValue(promotion.name)}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded ${getTypeColor(promotion.type)}`}>
                          {t(`promotions.${promotion.type}`)}
                        </span>
                        {promotion.isActive && !isExpired(promotion.endDate) && !isUpcoming(promotion.startDate) && (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                            {t('promotions.active')}
                          </span>
                        )}
                        {isExpired(promotion.endDate) && (
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                            {t('promotions.expired')}
                          </span>
                        )}
                        {isUpcoming(promotion.startDate) && (
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-600">
                            {t('promotions.upcoming')}
                          </span>
                        )}
                      </div>

                      {promotion.description && (
                        <div className="text-sm text-muted-foreground mt-1">
                          {getLangValue(promotion.description)}
                        </div>
                      )}

                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                        <div className="flex items-center gap-1">
                          <Tag className="h-3 w-3" />
                          {getDiscountLabel(promotion)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(promotion.startDate)} - {formatDate(promotion.endDate)}
                        </div>
                        {promotion.usageLimit && (
                          <div>
                            {promotion.usageCount}/{promotion.usageLimit} {t('promotions.used')}
                          </div>
                        )}
                        {promotion.autoApply && (
                          <div className="text-blue-600">{t('promotions.autoApply')}</div>
                        )}
                        {promotion.couponCode && (
                          <div className="font-mono font-medium">
                            {t('promotions.code')}: {promotion.couponCode}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewDetail(promotion)}
                        className="p-2 hover:bg-secondary rounded transition-colors"
                        title={t('common.view')}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {canManage && (
                        <>
                          <button
                            onClick={() => handleToggle(promotion)}
                            className="p-1 hover:bg-secondary rounded transition-colors"
                            title={t('promotions.toggleActive')}
                            disabled={toggleMutation.isPending}
                          >
                            {promotion.isActive ? (
                              <ToggleRight className="h-5 w-5 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(promotion)}
                            disabled={deleteMutation.isPending}
                            className="p-1 hover:bg-destructive/10 text-destructive rounded transition-colors"
                            title={t('common.delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {filteredPromotions.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Tag className="h-16 w-16 mx-auto mb-4 opacity-20" />
              <p>{t('promotions.noPromotions')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <PromotionModal
          promotion={null}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showEditModal && selectedPromotion && (
        <PromotionModal
          promotion={selectedPromotion}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPromotion(null);
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {showDetailModal && selectedPromotion && (
        <PromotionDetailModal
          promotion={selectedPromotion}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedPromotion(null);
          }}
          onEdit={() => handleEdit(selectedPromotion)}
        />
      )}
    </div>
  );
}

export default PromotionsPage;
